/**
 * ShareInviteButton Component
 *
 * Phase 1 of the shareable join link (#26). A member-only button that, on
 * click, asks the backend for an invite token for the active ecosystem and
 * copies an absolute, tenant-aware join link to the clipboard, e.g.
 * https://rileconnect.dev/costem?invite=abc123
 *
 * The link points at the ecosystem LANDING PAGE (not signup) per Dr. Rajan's
 * direction, carrying the token as a `?invite=` query param. What happens when
 * a recipient follows it — the invite banner, auto-join, signup-then-join — is
 * Phase 2 (#171) and intentionally NOT handled here.
 *
 * Visibility: rendered only for active members of the current ecosystem
 * (the link invites people into THIS ecosystem), so it returns null in
 * platform mode and for non-members.
 *
 * Transparency (#203): when the link is copied we pop up a brief
 * note telling the member that whoever opens the link will see their name in
 * "First L" form (e.g. "Michael J") on the invite banner — matching what the
 * recipient actually sees (see InviteBanner + invite.controller formatInviterName).
 *
 * Lives in components/common/ (moved here from features/dashboard/components/
 * in #275) alongside JoinEcosystemBanner, the other membership-related widget
 * that isn't scoped to one feature: its only consumer is Directory today, but
 * nothing about it is dashboard-specific, so ownership now matches usage.
 */
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Button, Overlay, Popover } from 'react-bootstrap';
import PropTypes from 'prop-types';

import { useEcosystem } from '@/shared/contexts/EcosystemContext';
import { useMembership } from '@/shared/contexts/MembershipContext';
import { useInviteApi } from '@/shared/services/inviteApi';
import { getAbsoluteEcosystemUrl } from '@/shared/utils/ecosystemRouting';

// How long the "Link copied!" confirmation stays before reverting.
const FEEDBACK_MS = 2500;
// The name-disclosure popover lingers a bit longer than the button feedback so
// there's time to actually read it before it fades.
const DISCLOSURE_MS = 6000;

export default function ShareInviteButton({
    label = 'Invite people',
    variant = 'outline-primary',
    size = 'sm',
    className = '',
}) {
    const { ecosystem } = useEcosystem();
    const { isMember } = useMembership();
    const inviteApi = useInviteApi();
    // 'idle' | 'loading' | 'copied' | 'error'
    const [status, setStatus] = useState('idle');
    // Controls the name-disclosure popover; kept separate from `status` so it
    // can linger after the button label reverts from "Link copied!".
    const [showDisclosure, setShowDisclosure] = useState(false);

    // The button is the popover's anchor; timer refs let us cancel pending
    // reverts so a rapid re-click doesn't leave a stale popover or label.
    const buttonRef = useRef(null);
    const statusTimer = useRef(null);
    const disclosureTimer = useRef(null);

    // Clear any in-flight timers on unmount so we never setState after teardown.
    useEffect(
        () => () => {
            clearTimeout(statusTimer.current);
            clearTimeout(disclosureTimer.current);
        },
        []
    );

    const handleClick = useCallback(async () => {
        setStatus('loading');
        try {
            const { token } = await inviteApi.generateInvite();
            const url = getAbsoluteEcosystemUrl(
                ecosystem,
                `?invite=${encodeURIComponent(token)}`
            );
            await navigator.clipboard.writeText(url);
            setStatus('copied');
            setShowDisclosure(true);
            clearTimeout(statusTimer.current);
            clearTimeout(disclosureTimer.current);
            statusTimer.current = setTimeout(
                () => setStatus('idle'),
                FEEDBACK_MS
            );
            disclosureTimer.current = setTimeout(
                () => setShowDisclosure(false),
                DISCLOSURE_MS
            );
        } catch (err) {
            console.error('Failed to generate invite link:', err);
            setStatus('error');
            // A failed click supersedes any lingering "copied" popover from a
            // prior success, so hide it rather than leaving it beside "Try again".
            setShowDisclosure(false);
            clearTimeout(statusTimer.current);
            clearTimeout(disclosureTimer.current);
            statusTimer.current = setTimeout(
                () => setStatus('idle'),
                FEEDBACK_MS
            );
        }
    }, [ecosystem, inviteApi]);

    // Member-only affordance: nothing to share at the platform apex or for
    // someone who is not a member of the current ecosystem.
    if (!isMember || !ecosystem) {
        return null;
    }

    const buttonLabel =
        status === 'loading'
            ? 'Generating…'
            : status === 'copied'
              ? 'Link copied!'
              : status === 'error'
                ? 'Try again'
                : label;

    // Shown on copy and (for screen readers) announced with the copy result, so
    // the name disclosure reaches everyone, not just sighted users.
    const disclosureText =
        'Your first name and last initial will be displayed.';

    return (
        <span className={`d-inline-flex align-items-center ${className}`}>
            <Button
                ref={buttonRef}
                variant={status === 'copied' ? 'success' : variant}
                size={size}
                className="rounded-pill"
                onClick={handleClick}
                disabled={status === 'loading'}
                title={`Copy an invite link to ${ecosystem.name || 'this ecosystem'}`}
            >
                {buttonLabel}
            </Button>

            {/* Name-disclosure popover: appears the moment the link is copied.
                It's purely informational and never blocks the copy (which has
                already happened by the time it shows). */}
            <Overlay
                target={buttonRef.current}
                show={showDisclosure}
                placement="bottom"
            >
                {(overlayProps) => (
                    <Popover id="invite-name-disclosure" {...overlayProps}>
                        <Popover.Body>{disclosureText}</Popover.Body>
                    </Popover>
                )}
            </Overlay>

            {/* Screen-reader announcement for the copy result + disclosure. */}
            <span className="visually-hidden" role="status" aria-live="polite">
                {status === 'copied' &&
                    `Invite link copied to clipboard. ${disclosureText}`}
                {status === 'error' && 'Could not generate the invite link'}
            </span>
        </span>
    );
}

ShareInviteButton.propTypes = {
    label: PropTypes.string,
    variant: PropTypes.string,
    size: PropTypes.string,
    className: PropTypes.string,
};
